/**
 * Deep links — the route grammar in one place:
 *
 *   /products/:pid                product home (new-chat empty state)
 *   /products/:pid/chats/:sid     one conversation (two-pane workspace)
 *   /products/:pid/settings       product settings
 *   /products/:pid/codebases      the product's codebases + live infrastructure
 *   /products/:pid/codebases/:rid one codebase's page
 *
 * Spelled out, not one-lettered (owner, 2026-09-02); the old `/p/` and
 * `/prod/` grammars redirect here (routes.tsx).
 *
 * Old `/prod/*` URLs redirect (routes.tsx LegacyRedirects). The product
 * scope rides the PATH; the repo context of a conversation is resolved by
 * SessionPage and mirrored into the route-scope store.
 *
 * The canvas search-param grammar (`mode`/`focus`/`flow`/`step`) retired
 * with the map (DECISIONS #62): a link addresses a page, and what the pane
 * shows is whatever the conversation drew.
 */

import type { Ulid } from '../api/types';

// ---------------------------------------------------------------------------
// Route builders (the v2 path grammar in one place)
// ---------------------------------------------------------------------------

/** The product home: the new-chat empty state (`/products/:pid`). */
export function productPath(pid: Ulid): string {
  return `/products/${pid}`;
}

/** One conversation (`/products/:pid/chats/:sid`). */
export function sessionPath(pid: Ulid, sid: Ulid): string {
  return `/products/${pid}/chats/${sid}`;
}

/** The product's settings page (basics, repos, danger zone). */
export function productSettingsPath(pid: Ulid): string {
  return `/products/${pid}/settings`;
}

/** One repo's settings page (the Codebase page), nested under settings. */
export function codebasePath(pid: Ulid, rid: Ulid): string {
  return `/products/${pid}/codebases/${rid}`;
}

/** The product's Codebases page: every connected codebase and its refresh state. */
export function codebasesPath(pid: Ulid): string {
  return `/products/${pid}/codebases`;
}

/** The product's Infrastructure page: the live sources, the runtime map, what the analysis found. */
export function infrastructurePath(pid: Ulid): string {
  return `/products/${pid}/infrastructure`;
}
